import { SocialCallback } from '@pulse/auth-ui';
import * as React from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { ProtectedRoute } from './auth/protected-route';
import { PulseShell } from './components/layout/pulse-shell';
import { InboxPage } from './domains/inbox/inbox-page';
import { FollowedMeetingsPage } from './domains/meetings/followed-meetings-page';
import { ProjectMeetingsPage } from './domains/meetings/project-meetings-page';
import { MilestonesPage } from './domains/milestones/milestones-page';
import { PeoplePage } from './domains/people/people-page';
import { PersonPage } from './domains/people/person-page';
import { PlanningPage } from './domains/people/planning-page';
import { ProjectAcceptancePage } from './domains/projects/project-acceptance-page';
import { ProjectBoardPage } from './domains/projects/project-board-page';
import { ProjectDomainsPage } from './domains/projects/project-domains-page';
import { ProjectMilestonesPage } from './domains/projects/project-milestones-page';
import { ProjectOverviewPage } from './domains/projects/project-overview-page';
import { ProjectsListPage } from './domains/projects/projects-list-page';
import { ReportsPage } from './domains/reports/reports-page';
import { ShippedReleasePage } from './domains/shipped/release-page';
import { ShippedPage } from './domains/shipped/shipped-page';
import { TalosPage } from './domains/talos/talos-page';
import { TodayPage } from './domains/today/today-page';
import { TrendsPage } from './domains/trends/trends-page';
import { TeamPage } from './domains/users/team-page';
import { LinkAccountPage } from './pages/link-account-page';
import { LoginPage } from './pages/login-page';

/**
 * Application route table.
 *
 * Sign-in routes sit outside the shell: Google or an emailed code, with no password, so no sign-up,
 * recovery or two-step pages (ACS-004). Every Pulse page sits inside the shell, behind a session.
 */
export function App(): React.ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<SocialCallbackRoute />} />
      <Route path="/auth/link-account" element={<LinkAccountPage />} />
      <Route
        element={
          <ProtectedRoute>
            <PulseShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<TodayPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="shipped" element={<ShippedPage />} />
        <Route path="shipped/releases/:repository/:number" element={<ShippedReleasePage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="people/planning" element={<PlanningPage />} />
        <Route path="people/team" element={<TeamPage />} />
        <Route path="people/:userId" element={<PersonPage />} />
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/meetings" element={<FollowedMeetingsPage />} />
        <Route path="projects/:productSlug" element={<ProjectOverviewPage />} />
        <Route path="projects/:productSlug/board" element={<ProjectBoardPage />} />
        <Route path="projects/:productSlug/milestones" element={<ProjectMilestonesPage />} />
        <Route path="projects/:productSlug/meetings" element={<ProjectMeetingsPage />} />
        <Route path="projects/:productSlug/domains" element={<ProjectDomainsPage />} />
        <Route path="projects/:productSlug/acceptance" element={<ProjectAcceptancePage />} />
        <Route path="milestones" element={<MilestonesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="trends" element={<TrendsPage />} />
        <Route path="talos" element={<TalosPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Bridges `<SocialCallback>` (auth-ui) to react-router's navigation. The callback runs the
 * exchange and reports back the original next-path; we push it via `navigate(replace)` so
 * the user doesn't have a stale `/auth/callback?code=...` entry in their history.
 */
function SocialCallbackRoute(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <SocialCallback
      onSuccess={(next) => navigate(next, { replace: true })}
      onLinkRequired={(flowId) =>
        navigate(`/auth/link-account?flow=${encodeURIComponent(flowId)}`, { replace: true })
      }
      onFailure={(error) => {
        navigate(`/login?error=${encodeURIComponent(error.message)}`, { replace: true });
      }}
    />
  );
}
